import React, { useState } from 'react';
import { Container, Form, Button, Card, Spinner } from 'react-bootstrap';
import { changePassword } from '../services/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import PasswordInput from '../components/PasswordInput';

const ChangePasswordPage = () => {
    const { t } = useTranslation();
    const [passwords, setPasswords] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPasswords(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setPasswordErrors([]); // Clear previous password errors
        if (passwords.new_password !== passwords.confirm_password) {
            toast.error(t('changePasswordPage.toasts.mismatch'));
            return;
        }
        setSubmitting(true);
        const toastId = toast.loading(t('changePasswordPage.toasts.changing'));

        try {
            await changePassword({
                current_password: passwords.current_password,
                new_password: passwords.new_password,
                confirm_new_password: passwords.confirm_password,
            });
            toast.success(t('changePasswordPage.toasts.success'), { id: toastId });
            setPasswords({
                current_password: '',
                new_password: '',
                confirm_password: '',
            });
        } catch (error) {
            console.error("Failed to change password", error);
            if (error.response && error.response.data && error.response.data.errors) {
                const errorMessages = error.response.data.errors.map(key => t(key));
                setPasswordErrors(errorMessages);
                toast.error(errorMessages.join(' '), { id: toastId, duration: 6000 });
            } else {
                const errorMsg = error.response?.data?.message || t('changePasswordPage.toasts.failed');
                toast.error(errorMsg, { id: toastId });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container className="mt-4">
            <Card>
                <Card.Header as="h2">{t('changePasswordPage.title')}</Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <PasswordInput
                            controlId="current_password"
                            label={t('changePasswordPage.currentPasswordLabel')}
                            name="current_password"
                            value={passwords.current_password}
                            onChange={handleChange}
                            placeholder={t('changePasswordPage.currentPasswordPlaceholder')}
                            required
                        />
                        <PasswordInput
                            controlId="new_password"
                            label={t('changePasswordPage.newPasswordLabel')}
                            name="new_password"
                            value={passwords.new_password}
                            onChange={handleChange}
                            placeholder={t('changePasswordPage.newPasswordPlaceholder')}
                            required
                        />
                        {passwordErrors.length > 0 && (
                            <Form.Text className="text-danger">
                                <ul className="list-unstyled">
                                    {passwordErrors.map((errorMsg, index) => (
                                        <li key={index}>{errorMsg}</li>
                                    ))}
                                </ul>
                            </Form.Text>
                        )}
                        <PasswordInput
                            controlId="confirm_password"
                            label={t('changePasswordPage.confirmPasswordLabel')}
                            name="confirm_password"
                            value={passwords.confirm_password}
                            onChange={handleChange}
                            placeholder={t('changePasswordPage.confirmPasswordPlaceholder')}
                            required
                        />
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> {t('changePasswordPage.changingButton')}</> : t('changePasswordPage.changeButton')}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ChangePasswordPage;